/**
 * CaseStudyArticle - Full case study presentation with an embedded
 * Power BI report, structured sections, findings, a summary-of-results
 * table, and a PDF download link.
 *
 * The article `id` is the smooth-scroll target used by CaseStudyCard;
 * scroll-margin-top clears the 4rem sticky header.
 */
import React from 'react';
import { Download } from 'lucide-react';

export interface CaseStudySection {
  heading: string;
  paragraphs: string[];
}

export interface CaseStudyFinding {
  title: string;
  body: string;
}

export interface CaseStudySummaryRow {
  label: string;
  value: string;
}

export interface CaseStudyArticleProps {
  id: string;
  title: string;
  subtitle?: string;
  sections: CaseStudySection[];
  embedSrc: string;
  embedTitle: string;
  findings: CaseStudyFinding[];
  summaryRows: CaseStudySummaryRow[];
  conclusion: string[];
  pdfHref?: string;
  className?: string;
}

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-2xl)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text)',
  marginTop: 'var(--spacing-10)',
  marginBottom: 'var(--spacing-4)',
};

const paragraphStyle: React.CSSProperties = {
  color: 'var(--color-text-muted)',
  lineHeight: 'var(--line-height-relaxed)',
  marginBottom: 'var(--spacing-4)',
};

const CaseStudyArticle: React.FC<CaseStudyArticleProps> = (props) => {
  const { id, title, subtitle, sections, embedSrc, embedTitle, findings, summaryRows, conclusion, pdfHref, className = '' } = props;

  return (
    <article
      id={id}
      className={`card-shell ${className}`}
      aria-label={title}
      style={{ padding: 'var(--spacing-8)', scrollMarginTop: 'calc(4rem + var(--spacing-4))' }}
    >
      {/* Header */}
      <header>
        <p style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 'var(--spacing-2)' }}>
          Case Study
        </p>
        <h2 style={{ fontSize: 'var(--font-size-3xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text)', marginBottom: 'var(--spacing-3)' }}>
          {title}
        </h2>
        {subtitle && (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-base)', fontFamily: 'var(--font-family-mono)', marginBottom: 'var(--spacing-2)' }}>
            {subtitle}
          </p>
        )}
      </header>

      {/* Narrative sections */}
      {sections.map((section) => (
        <section key={section.heading} aria-label={section.heading}>
          <h3 style={sectionHeadingStyle}>{section.heading}</h3>
          {section.paragraphs.map((text, i) => (
            <p key={i} style={paragraphStyle}>{text}</p>
          ))}
        </section>
      ))}

      {/* Live Power BI report */}
      <section aria-label={`${title} interactive report`}>
        <h3 style={sectionHeadingStyle}>Explore the Live Report</h3>
        <p style={paragraphStyle}>
          The interactive Power BI report below is the actual production deliverable. Use the page
          navigation inside the report to explore each exhibit referenced in the findings.
        </p>
        <div
          style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '62.25%',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            border: '1px solid var(--color-border)',
            boxShadow: 'var(--shadow-md)',
            backgroundColor: 'var(--color-surface-2)',
          }}
        >
          <iframe
            title={embedTitle}
            src={embedSrc}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
            allowFullScreen
            loading="lazy"
          />
        </div>
      </section>

      {/* Findings */}
      <section aria-label={`${title} findings`}>
        <h3 style={sectionHeadingStyle}>Findings</h3>
        {findings.map((finding, i) => (
          <div key={i} style={{ marginBottom: 'var(--spacing-6)' }}>
            <h4 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text)', marginBottom: 'var(--spacing-2)' }}>
              {finding.title}
            </h4>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>{finding.body}</p>
          </div>
        ))}
      </section>

      {/* Summary of results */}
      <section aria-label={`${title} summary of results`}>
        <h3 style={sectionHeadingStyle}>Summary of Results</h3>
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          {summaryRows.map((row, i) => (
            <div
              key={row.label}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(140px, 1fr) 2fr',
                gap: 'var(--spacing-4)',
                padding: 'var(--spacing-4) var(--spacing-5)',
                borderTop: i > 0 ? '1px solid var(--color-border)' : 'none',
                backgroundColor: i % 2 === 0 ? 'var(--color-surface)' : 'var(--color-background)',
              }}
            >
              <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text)' }}>
                {row.label}
              </span>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', lineHeight: 'var(--line-height-relaxed)' }}>
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Conclusion */}
      <section aria-label={`${title} conclusion`}>
        <h3 style={sectionHeadingStyle}>Conclusion</h3>
        {conclusion.map((text, i) => (
          <p key={i} style={paragraphStyle}>{text}</p>
        ))}
      </section>

      {pdfHref && (
        <footer style={{ marginTop: 'var(--spacing-8)', display: 'flex', justifyContent: 'center' }}>
          <a href={pdfHref} className="btn btn-secondary" download>
            <Download size={16} aria-hidden="true" />
            Download the Full Case Study (PDF)
          </a>
        </footer>
      )}
    </article>
  );
};

export default CaseStudyArticle;
